import { useState, useEffect } from 'react';
import StatCard from '../../components/common/StatCard';
import Button from '../../components/common/Button';
import Icon from '../../components/common/Icon';
import './LazyMarketing.css';

const Inbox = () => {
  const [emails, setEmails] = useState([]);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [aiSettings, setAiSettings] = useState({
    autoRespond: false,
    requireApproval: true,
    tone: 'professional',
    maxResponseTime: 24
  });
  const [generatingResponse, setGeneratingResponse] = useState(false);
  const [aiResponse, setAiResponse] = useState('');

  useEffect(() => {
    fetchEmails();
  }, [filter]);

  const fetchEmails = async () => {
    setLoading(true);
    // Mock data - will wire to backend API
    await new Promise(resolve => setTimeout(resolve, 500));

    setEmails([
      {
        id: 1,
        from: 'john.smith@techcorp.com',
        fromName: 'John Smith',
        subject: 'Re: Quick question about your services',
        preview: 'Hi, thanks for reaching out! I\'d love to learn more about what you offer...',
        body: 'Hi,\n\nThanks for reaching out! I\'d love to learn more about what you offer. We\'re currently looking for solutions in this space.\n\nCould you tell me more about pricing and implementation timeline?\n\nBest,\nJohn Smith\nVP of Operations, TechCorp',
        date: '10 min ago',
        campaign: 'Q1 Outreach',
        sentiment: 'positive',
        category: 'interested',
        replied: false,
        starred: true
      },
      {
        id: 2,
        from: 'sarah@startup.io',
        fromName: 'Sarah Lee',
        subject: 'Re: Partnership opportunity',
        preview: 'Not interested at this time, but feel free to follow up next quarter...',
        body: 'Hi,\n\nThanks for thinking of us, but we\'re not looking for new solutions at this time. Our current setup works well for our needs.\n\nFeel free to follow up next quarter when we\'re reviewing our stack.\n\nBest,\nSarah',
        date: '1 hour ago',
        campaign: 'Partner Outreach',
        sentiment: 'neutral',
        category: 'not_interested',
        replied: false,
        starred: false
      },
      {
        id: 3,
        from: 'mike.johnson@enterprise.com',
        fromName: 'Mike Johnson',
        subject: 'Re: Improving your marketing ROI',
        preview: 'I\'m out of office until January 15th. For urgent matters...',
        body: 'Thank you for your email. I\'m currently out of office until January 15th, 2026.\n\nFor urgent matters, please contact support@enterprise.com.\n\nI\'ll respond to your email upon my return.\n\nBest regards,\nMike Johnson',
        date: '2 hours ago',
        campaign: 'Q1 Outreach',
        sentiment: 'neutral',
        category: 'ooo',
        replied: true,
        starred: false
      },
      {
        id: 4,
        from: 'emily.davis@corp.com',
        fromName: 'Emily Davis',
        subject: 'Re: Let\'s connect',
        preview: 'This sounds great! Can we schedule a call this week?',
        body: 'Hi!\n\nThis sounds really interesting. I\'d love to learn more about how this could help our team.\n\nAre you available for a 15-minute call this week? I\'m free:\n- Tuesday 2-4pm EST\n- Thursday 10am-12pm EST\n\nLet me know what works for you!\n\nEmily Davis\nHead of Marketing, Corp Inc.',
        date: '3 hours ago',
        campaign: 'Cold Email v2',
        sentiment: 'positive',
        category: 'interested',
        replied: false,
        starred: true
      },
      {
        id: 5,
        from: 'alex@smallbiz.com',
        fromName: 'Alex Brown',
        subject: 'Re: Quick question',
        preview: 'Please remove me from your mailing list...',
        body: 'Please remove me from your mailing list. I\'m not interested.\n\nThanks,\nAlex',
        date: '5 hours ago',
        campaign: 'Q1 Outreach',
        sentiment: 'negative',
        category: 'unsubscribe',
        replied: true,
        starred: false
      }
    ]);
    setLoading(false);
  };

  const handleGenerateAIResponse = async () => {
    if (!selectedEmail) return;
    setGeneratingResponse(true);

    // Simulate AI response generation
    await new Promise(resolve => setTimeout(resolve, 1500));

    const responses = {
      interested: `Hi ${selectedEmail.fromName.split(' ')[0]},

Thank you for your interest! I'd be happy to tell you more about our offering.

We typically work with companies of your size and have helped similar organizations achieve 30-40% improvements in their key metrics.

I'd love to schedule a quick 15-minute call to understand your specific needs. Would any of these times work for you?
- Tomorrow at 2pm EST
- Thursday at 10am EST

Looking forward to connecting!

Best regards`,
      not_interested: `Hi ${selectedEmail.fromName.split(' ')[0]},

I completely understand - timing is everything. I appreciate you taking the time to respond.

I'll make a note to follow up next quarter as you suggested. In the meantime, if anything changes or you'd like to explore options sooner, feel free to reach out.

Wishing you continued success!

Best regards`,
      ooo: `Noted - I'll follow up after January 15th when ${selectedEmail.fromName.split(' ')[0]} is back.`,
      unsubscribe: `Hi ${selectedEmail.fromName.split(' ')[0]},

I've removed you from our mailing list as requested. You won't receive any further emails from us.

I apologize for any inconvenience.

Best regards`
    };

    setAiResponse(responses[selectedEmail.category] || responses.interested);
    setGeneratingResponse(false);
  };

  const handleSendResponse = () => {
    // API call to send response
    alert('Response sent!');
    setSelectedEmail(null);
    setAiResponse('');
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'interested': return 'thumbs-up';
      case 'not_interested': return 'thumbs-down';
      case 'ooo': return 'calendar';
      case 'unsubscribe': return 'x-circle';
      default: return 'mail';
    }
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'interested': return 'var(--success)';
      case 'not_interested': return 'var(--warning)';
      case 'ooo': return 'var(--primary)';
      case 'unsubscribe': return 'var(--danger)';
      default: return 'var(--text-tertiary)';
    }
  };

  const filteredEmails = emails.filter(e => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !e.replied;
    if (filter === 'starred') return e.starred;
    return e.category === filter;
  });

  const stats = [
    { label: 'Total Replies', value: emails.length.toString(), change: '+12 today', changeType: 'positive' },
    { label: 'Interested', value: emails.filter(e => e.category === 'interested').length.toString(), change: 'Hot leads', changeType: 'positive' },
    { label: 'Pending Response', value: emails.filter(e => !e.replied).length.toString(), change: 'Need attention', changeType: 'warning' },
    { label: 'Response Rate', value: '34%', change: '+2.1% from last week', changeType: 'positive' },
  ];

  return (
    <div className="lm-page inbox-page">
      <div className="lm-page-header">
        <div>
          <h1 className="lm-page-title">AI Inbox</h1>
          <p className="lm-page-subtitle">Manage replies with AI-powered responses</p>
        </div>
        <div className="header-actions">
          <Button variant="secondary" icon="settings">AI Settings</Button>
          <Button variant="primary" icon="refresh" onClick={fetchEmails}>Refresh</Button>
        </div>
      </div>

      <div className="lm-stats-row">
        {stats.map((stat, index) => (
          <StatCard key={index} {...stat} loading={loading} />
        ))}
      </div>

      <div className="inbox-container">
        {/* Email List */}
        <div className="inbox-list">
          <div className="inbox-filters">
            <button className={`filter-btn ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>All</button>
            <button className={`filter-btn ${filter === 'unread' ? 'active' : ''}`} onClick={() => setFilter('unread')}>Unread</button>
            <button className={`filter-btn ${filter === 'interested' ? 'active' : ''}`} onClick={() => setFilter('interested')}>Interested</button>
            <button className={`filter-btn ${filter === 'starred' ? 'active' : ''}`} onClick={() => setFilter('starred')}>Starred</button>
          </div>

          <div className="email-list">
            {loading ? (
              <div className="loading-state">Loading emails...</div>
            ) : filteredEmails.map(email => (
              <div
                key={email.id}
                className={`email-item ${selectedEmail?.id === email.id ? 'selected' : ''} ${!email.replied ? 'unread' : ''}`}
                onClick={() => setSelectedEmail(email)}
              >
                <div className="email-indicator">
                  <Icon name={getCategoryIcon(email.category)} style={{ color: getCategoryColor(email.category) }} />
                </div>
                <div className="email-content">
                  <div className="email-header">
                    <span className="email-from">{email.fromName}</span>
                    <span className="email-date">{email.date}</span>
                  </div>
                  <div className="email-subject">{email.subject}</div>
                  <div className="email-preview">{email.preview}</div>
                  <div className="email-meta">
                    <span className="email-campaign">{email.campaign}</span>
                    <span className={`email-category ${email.category}`}>{email.category.replace('_', ' ')}</span>
                  </div>
                </div>
                {email.starred && <Icon name="star" className="email-star" />}
              </div>
            ))}
          </div>
        </div>

        {/* Email Detail */}
        <div className="inbox-detail">
          {selectedEmail ? (
            <>
              <div className="detail-header">
                <div className="detail-info">
                  <h3>{selectedEmail.subject}</h3>
                  <div className="detail-meta">
                    <span>From: {selectedEmail.from}</span>
                    <span>Campaign: {selectedEmail.campaign}</span>
                    <span className={`category-badge ${selectedEmail.category}`}>
                      <Icon name={getCategoryIcon(selectedEmail.category)} />
                      {selectedEmail.category.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <div className="detail-actions">
                  <button className="action-btn" title="Star">
                    <Icon name={selectedEmail.starred ? 'star' : 'star'} />
                  </button>
                  <button className="action-btn" title="Archive">
                    <Icon name="archive" />
                  </button>
                  <button className="action-btn danger" title="Delete">
                    <Icon name="trash-2" />
                  </button>
                </div>
              </div>

              <div className="detail-body">
                <pre>{selectedEmail.body}</pre>
              </div>

              <div className="response-section">
                <div className="response-header">
                  <h4>AI Response</h4>
                  <Button
                    variant="secondary"
                    size="sm"
                    icon="zap"
                    onClick={handleGenerateAIResponse}
                    disabled={generatingResponse}
                  >
                    {generatingResponse ? 'Generating...' : 'Generate with AI'}
                  </Button>
                </div>
                <textarea
                  className="response-textarea"
                  placeholder="Click 'Generate with AI' or write your response..."
                  value={aiResponse}
                  onChange={(e) => setAiResponse(e.target.value)}
                />
                <div className="response-actions">
                  <Button variant="secondary" size="sm" onClick={() => setAiResponse('')}>
                    Clear
                  </Button>
                  <Button variant="primary" icon="send" onClick={handleSendResponse} disabled={!aiResponse}>
                    Send Response
                  </Button>
                </div>
              </div>
            </>
          ) : (
            <div className="no-email-selected">
              <Icon name="mail" />
              <p>Select an email to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inbox;
